import React, { useEffect } from 'react';
import { Button, Space, Typography, message, Select, Spin } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchNewsletter, updateNewsletterContent, uploadNewsletterImage, deleteNewsletterImage } from './api';

import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import Image from '@tiptap/extension-image';
import LineHeight from './tiptap/LineHeight';

const NewsletterEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [newsletter, setNewsletter] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const fileInputRef = React.useRef(null);

    const previousImagesRef = React.useRef([]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: true, autolink: true }),
      Highlight,
      Superscript,
      Subscript,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: { style: 'max-width:100%' },
      }),
      LineHeight,
    ],
    content: '',
    onUpdate: ({ editor }) => {
      try {
        const json = editor.getJSON();
        const images = [];
        const walk = (node) => {
          if (!node) return;
          if (node.type === 'image' && node.attrs?.src) {
            images.push({ src: node.attrs.src, key: node.attrs['data-key'] });
          }
          if (Array.isArray(node.content)) node.content.forEach(walk);
        };
        walk(json);
        if (!Array.isArray(previousImagesRef.current)) previousImagesRef.current = [];
        const removed = previousImagesRef.current.filter(
          (prev) => !images.some((curr) => curr.key === prev.key)
        );
        previousImagesRef.current = images;
        removed.forEach((img) => {
          deleteNewsletterImage(img.key);
        });
      } catch (e) {}
    },
  });

  const load = async () => {
    const b = await fetchNewsletter(slug);
    if (b) {
      setNewsletter(b);
      editor?.commands.setContent(b.content || '');
      try {
        const json = editor?.getJSON();
        const images = [];
        const walk = (node) => {
          if (!node) return;
          if (node.type === 'image' && node.attrs?.src) {
            images.push({ src: node.attrs.src, key: node.attrs['data-key'] });
          }
          if (Array.isArray(node.content)) node.content.forEach(walk);
        };
        walk(json);
        previousImagesRef.current = images;
      } catch {}
    }
    setLoading(false);
  };

  useEffect(() => {
    if (editor) {
      load();
    }
  }, [slug, editor]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    if (!newsletter?.id) return;
    const html = editor?.getHTML() || '';
    try {
      const ok = await updateNewsletterContent({ id: newsletter.id, content: html });
      if (ok) {
        message.success('Newsletter content saved');
      } else {
        message.error('Failed to save content');
      }
    } finally {
      setSaving(false);
    }
  };

  const headingOptions = [
    { label: 'Paragraph', value: 'paragraph' },
    { label: 'H1', value: '1' },
    { label: 'H2', value: '2' },
    { label: 'H3', value: '3' },
  ];

  const setHeading = (value) => {
    if (!editor) return;
    if (value === 'paragraph') {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level: parseInt(value) }).run();
    }
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  const handleImageSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const hide = message.loading('Uploading image...', 0);
    try {
      const { url, key } = await uploadNewsletterImage(file);
      if (url) {
        editor?.chain().focus().setImage({ src: url, alt: 'newsletter-image', 'data-key': key }).run();
        message.success('Image inserted');
      } else {
        message.error('Upload failed');
      }
    } catch {
      message.error('Upload failed');
    } finally {
      hide();
    }
  };

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', padding: 16, overflow: 'hidden' }}>
      <Space style={{ marginBottom: 12 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>{newsletter?.heading || 'Newsletter Editor'}</Typography.Title>
        <Button type="primary" onClick={handleSave} loading={saving}>Save</Button>
        <Button onClick={() => navigate(-1)}>Back</Button>
      </Space>
      <Space style={{ marginBottom: 12 }}>
        <Button type={editor?.isActive('bold') ? 'primary' : 'default'} onClick={() => editor?.chain().focus().toggleBold().run()}>Bold</Button>
        <Button type={editor?.isActive('italic') ? 'primary' : 'default'} onClick={() => editor?.chain().focus().toggleItalic().run()}>Italic</Button>
        <Button type={editor?.isActive('underline') ? 'primary' : 'default'} onClick={() => editor?.chain().focus().toggleUnderline().run()}>Underline</Button>
        <Button type={editor?.isActive('strike') ? 'primary' : 'default'} onClick={() => editor?.chain().focus().toggleStrike().run()}>Strike</Button>
        <Button onClick={() => editor?.chain().focus().toggleBulletList().run()} type={editor?.isActive('bulletList') ? 'primary' : 'default'}>Bulleted List</Button>
        <Button onClick={() => editor?.chain().focus().toggleOrderedList().run()} type={editor?.isActive('orderedList') ? 'primary' : 'default'}>Numbered List</Button>
        <Button onClick={() => editor?.chain().focus().toggleBlockquote().run()} type={editor?.isActive('blockquote') ? 'primary' : 'default'}>Quote</Button>
        <Button onClick={() => editor?.chain().focus().setHorizontalRule().run()}>HR</Button>
        <Select
          defaultValue="paragraph"
          onChange={setHeading}
          options={headingOptions}
          style={{ width: 140 }}
        />
        <Select
          defaultValue="left"
          onChange={(val) => editor?.chain().focus().setTextAlign(val).run()}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
            { label: 'Justify', value: 'justify' },
          ]}
          style={{ width: 120 }}
        />
        <Select
          defaultValue="normal"
          onChange={(val) => {
            if (!editor) return;
            if (val === 'normal') {
              editor.chain().focus().unsetLineHeight().run();
            } else {
              editor.chain().focus().setLineHeight(val).run();
            }
          }}
          options={[
            { label: 'Line: Normal', value: 'normal' },
            { label: 'Line: 1.2', value: '1.2' },
            { label: 'Line: 1.4', value: '1.4' },
            { label: 'Line: 1.6', value: '1.6' },
            { label: 'Line: 1.8', value: '1.8' },
            { label: 'Line: 2.0', value: '2' },
          ]}
          style={{ width: 150 }}
        />
        <Button type={editor?.isActive('highlight') ? 'primary' : 'default'} onClick={() => editor?.chain().focus().toggleHighlight().run()}>Highlight</Button>
        <Button type={editor?.isActive('link') ? 'primary' : 'default'} onClick={() => {
          const url = window.prompt('Enter URL');
          if (url) editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }}>Link</Button>
        <Button onClick={() => editor?.chain().focus().unsetLink().run()} disabled={!editor?.isActive('link')}>Unlink</Button>
        <Button type={editor?.isActive('superscript') ? 'primary' : 'default'} onClick={() => editor?.chain().focus().toggleSuperscript().run()}>Superscript</Button>
        <Button type={editor?.isActive('subscript') ? 'primary' : 'default'} onClick={() => editor?.chain().focus().toggleSubscript().run()}>Subscript</Button>
        <Button onClick={triggerImageUpload}>Insert Image</Button>
        <Button onClick={() => editor?.chain().focus().undo().run()}>Undo</Button>
        <Button onClick={() => editor?.chain().focus().redo().run()}>Redo</Button>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleImageSelected}
          style={{ display: 'none' }}
        />
      </Space>
      <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, padding: 12, flex: 1, overflowY: 'auto' }}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default NewsletterEditor;
